/**
 * Pricing Page Component
 * 
 * Main pricing page with plans, features, and subscription management.
 */

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import PricingTable from '../../components/subscription/PricingTable';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { 
    error, 
    refreshSubscription 
  } = useSubscription();

  // Handle URL parameters for success/error states
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const error = searchParams.get('error');

    if (success === 'true') {
      // Handle successful payment
      refreshSubscription();
      // Clear URL params
      navigate('/subscription/pricing', { replace: true });
    } else if (cancelled === 'true') {
      // Handle cancelled checkout
      // Could show a message here
      navigate('/subscription/pricing', { replace: true });
    } else if (error) {
      // Handle checkout error
      console.error('Checkout error:', error);
      navigate('/subscription/pricing', { replace: true });
    }
  }, [searchParams, navigate, refreshSubscription]);

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Powerful AI Audience Research
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get deep insights into your target audience with AI-powered simulations.
            </p>
          </div>

          <PricingTable />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header for Authenticated Users */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Manage Subscription
          </h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-3" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Pricing Table */}
        <PricingTable />
      </div>
    </Layout>
  );
};

export default PricingPage;